/**
 * LOCKER-522 — print a reservation's deposit code (the code the clerk enters at
 * the locker to drop the parcel off) on a Zebra label printer through Zebra
 * Browser Print, as soon as the reservation is created.
 *
 * Shared by the backend (default Configuration rows, seed9) and the console
 * (rendering + printing). Rendering is pure so it can be unit-tested without a
 * printer.
 */

export const DEPOSIT_CODE_AUTO_PRINT_CONFIG_REF_NAME =
  "configDepositCodeAutoPrint";

/** `sVal` holds the tenant's ZPL. Empty or absent means the built-in default. */
export const DEPOSIT_CODE_ZPL_TEMPLATE_CONFIG_REF_NAME =
  "configDepositCodeZplTemplate";

/** `nVal`, in printer dots (8 per mm at 203 dpi). Positive = right / down. */
export const DEPOSIT_CODE_LABEL_OFFSET_X_CONFIG_REF_NAME =
  "configDepositCodeLabelOffsetX";
export const DEPOSIT_CODE_LABEL_OFFSET_Y_CONFIG_REF_NAME =
  "configDepositCodeLabelOffsetY";
export const DEPOSIT_CODE_LABEL_OFFSET_MAX_DOTS = 200;

export const DepositCodeZplVariableList = [
  "depositCode",
  "receptionCode",
  "doorLabel",
  "lockerName",
  "reservationNumber",
  "expiration",
  "organizationName",
  "printedAt",
] as const;

export type DepositCodeZplVariable =
  (typeof DepositCodeZplVariableList)[number];

export type DepositCodeZplValues = Partial<
  Record<DepositCodeZplVariable, string | number | undefined | null>
>;

/**
 * Built for the 2" x 1" label tested on QA, at 203 dpi (406 x 203 dots): the
 * deposit code, centred and large, with its door and reservation number. The
 * first default (2.25" x 1.25", with expiration and a barcode) ran past the
 * end of a 2" x 1" label onto the next one (QA, 2026-11-03).
 *
 * No `^PW` / `^LL` / `^LT` / `^LS`: those can stay set on the printer and would
 * move every other application's labels too. Position is corrected per tenant
 * with the label offsets instead (`applyZplLabelOffset`). `^CI28` switches the
 * field data to UTF-8 so accented text prints.
 */
export const DEFAULT_DEPOSIT_CODE_ZPL_TEMPLATE = [
  "^XA",
  "^CI28",
  "^FO10,12^A0N,22,22^FB386,1,0,C^FDCode de dépôt / Deposit code^FS",
  "^FO10,44^A0N,100,90^FB386,1,0,C^FD{{depositCode}}^FS",
  "^FO10,158^A0N,30,30^FB386,1,0,C^FDPorte / Door {{doorLabel}}   #{{reservationNumber}}^FS",
  "^XZ",
].join("\n");

const VARIABLE_PATTERN = /\{\{\s*([A-Za-z]+)\s*\}\}/g;

/**
 * A substituted value is field data, never a command: `^` and `~` start ZPL
 * commands, so a value carrying them (a locker named "A^XZ…") could end the
 * label or reconfigure the printer. They become spaces; control characters go.
 */
export const sanitizeZplFieldValue = (value: unknown): string =>
  value === undefined || value === null
    ? ""
    : String(value)
        .replace(/[\^~]/g, " ")
        // eslint-disable-next-line no-control-regex
        .replace(/[\u0000-\u001f\u007f]/g, " ")
        .trim();

/** The template to print: the tenant override when it holds any ZPL. */
export const resolveDepositCodeZplTemplate = (override?: string | null) =>
  override && override.trim() ? override : DEFAULT_DEPOSIT_CODE_ZPL_TEMPLATE;

/** Replaces `{{variable}}`; unknown variables render empty. */
export const renderDepositCodeZpl = (
  template: string,
  values: DepositCodeZplValues,
): string =>
  template.replace(VARIABLE_PATTERN, (_match, name: string) =>
    (DepositCodeZplVariableList as readonly string[]).includes(name)
      ? sanitizeZplFieldValue(values[name as DepositCodeZplVariable])
      : "",
  );

const FIELD_ORIGIN_PATTERN = /\^(FO|FT)(-?\d+),(-?\d+)/g;

const clampOffset = (n: unknown) => {
  const v = Math.round(Number(n) || 0);
  return Math.max(
    -DEPOSIT_CODE_LABEL_OFFSET_MAX_DOTS,
    Math.min(DEPOSIT_CODE_LABEL_OFFSET_MAX_DOTS, v),
  );
};

/**
 * Moves every field (`^FO` / `^FT` origin) by the tenant's offset, in dots.
 * Done on the text rather than with `^LH` / `^LT` / `^LS` so nothing is left set
 * on a printer that other applications share. A field pushed past the edge
 * stops at 0.
 */
export const applyZplLabelOffset = (
  zpl: string,
  offsetX?: number | null,
  offsetY?: number | null,
): string => {
  const dx = clampOffset(offsetX);
  const dy = clampOffset(offsetY);
  if (!dx && !dy) return zpl;
  return zpl.replace(
    FIELD_ORIGIN_PATTERN,
    (_m, cmd: string, x: string, y: string) =>
      `^${cmd}${Math.max(0, Number(x) + dx)},${Math.max(0, Number(y) + dy)}`,
  );
};
